// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

// material ui
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// api
import type {
  GetDeviceDto,
  PutDeviceTagDto,
} from '@/api/generated/recalc/endpoints/v2/devices';
import { usePutDeviceMutation } from '@/api/generated/recalc/endpoints/v2/devices';

// shared components
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

interface DeviceGeneralInfoDrawerProps {
  open: boolean;
  device?: GetDeviceDto | null;
  onClose: () => void;
}

type DeviceGeneralInfoDrawerFormProps = {
  device?: GetDeviceDto | null;
  onClose: () => void;
};

type EditableTag = {
  key: string;
  value: string;
};

function mapDeviceTagsToEditableTags(
  device?: GetDeviceDto | null,
): EditableTag[] {
  const existingTags = device?.tags ?? [];

  if (existingTags.length === 0) {
    return [];
  }

  return existingTags.map((tag) => ({
    key: tag?.key?.trim() ?? '',
    value: tag?.value?.trim() ?? '',
  }));
}

const DeviceGeneralInfoDrawerForm = ({
  device,
  onClose,
}: DeviceGeneralInfoDrawerFormProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const [putDevice, { isLoading: isSaving }] = usePutDeviceMutation();

  const [displayName, setDisplayName] = useState(
    device?.displayName?.trim() ?? '',
  );
  const [description, setDescription] = useState(
    device?.description?.trim() ?? '',
  );
  const [tags, setTags] = useState<EditableTag[]>(() =>
    mapDeviceTagsToEditableTags(device),
  );

  const handleAddTag = () => {
    setTags((previous) => [...previous, { key: '', value: '' }]);
  };

  const handleRemoveTag = (indexToRemove: number) => {
    setTags((previous) =>
      previous.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleTagChange = (
    indexToUpdate: number,
    field: keyof EditableTag,
    value: string,
  ) => {
    setTags((previous) =>
      previous.map((tag, index) =>
        index === indexToUpdate ? { ...tag, [field]: value } : tag,
      ),
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!device?.deviceId) {
      return;
    }

    if (!displayName.trim()) {
      dispatch(
        showSnackbar({
          message: t('devices.displayNameRequired'),
          severity: 'error',
        }),
      );
      return;
    }

    const normalizedTags = tags.map((tag) => ({
      key: tag.key.trim(),
      value: tag.value.trim(),
    }));

    const hasIncompleteTag = normalizedTags.some(
      (tag) => Boolean(tag.key) !== Boolean(tag.value),
    );

    if (hasIncompleteTag) {
      dispatch(
        showSnackbar({
          message: t('devices.tagsValidationRequired'),
          severity: 'error',
        }),
      );
      return;
    }

    const validTags: PutDeviceTagDto[] = normalizedTags.filter(
      (tag) => tag.key && tag.value,
    );

    const result = await putDevice({
      deviceId: device.deviceId,
      putDeviceDto: {
        displayName: displayName.trim(),
        description: description.trim() || null,
        tags: validTags.length > 0 ? validTags : null,
      },
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('devices.updatedSuccess', { name: displayName.trim() }),
          severity: 'success',
        }),
      );

      onClose();
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 1, py: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {t('devices.editGeneralInformationDescription', {
            defaultValue: 'Update name, description and tags for this device.',
          })}
        </Typography>
      </Box>

      <Box
        id="device-general-info-form"
        component="form"
        onSubmit={handleSubmit}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 1,
          py: 1.5,
        }}
      >
        <Stack spacing={3} mb={20}>
          <TextField
            required
            fullWidth
            label={t('devices.displayName')}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('devices.description')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />

          <Stack spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="overline" color="text.secondary">
                {t('devices.tags')}
              </Typography>

              <Button
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={handleAddTag}
              >
                {t('devices.addTag')}
              </Button>
            </Stack>

            {tags.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('common.notAvailable')}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {tags.map((tag, index) => (
                  <Stack
                    key={`tag-${index}`}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <TextField
                      fullWidth
                      label={t('devices.tagKey')}
                      value={tag.key}
                      onChange={(event) =>
                        handleTagChange(index, 'key', event.target.value)
                      }
                    />
                    <TextField
                      fullWidth
                      label={t('devices.tagValue')}
                      value={tag.value}
                      onChange={(event) =>
                        handleTagChange(index, 'value', event.target.value)
                      }
                    />
                    <IconButton
                      aria-label={t('devices.removeTag')}
                      onClick={() => handleRemoveTag(index)}
                      sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                    >
                      <DeleteOutlineRoundedIcon />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onClose} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          form="device-general-info-form"
          variant="contained"
          disabled={isSaving}
        >
          {t('common.save')}
        </Button>
      </Stack>
    </Box>
  );
};

const DeviceGeneralInfoDrawer = ({
  open,
  device,
  onClose,
}: DeviceGeneralInfoDrawerProps) => {
  const { t } = useTranslation('units-gateways-sensors');

  const formResetKey = [
    device?.deviceId ?? 'unknown-device',
    device?.displayName?.trim() ?? '',
    device?.description?.trim() ?? '',
    ...(device?.tags ?? []).map(
      (tag) => `${tag?.key?.trim() ?? ''}:${tag?.value?.trim() ?? ''}`,
    ),
    open ? 'open' : 'closed',
  ].join('|');

  return (
    <InsetDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      title={t('devices.editGeneralInformation')}
      width={760}
    >
      {open ? (
        <DeviceGeneralInfoDrawerForm
          key={formResetKey}
          device={device}
          onClose={onClose}
        />
      ) : null}
    </InsetDrawer>
  );
};

export default DeviceGeneralInfoDrawer;
